import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import { leagueService } from '../../services/leagueService';
import { filterPublicCompetitions } from '../../utils/publicCompetitionVisibility';

const statusBadgeClass = (status) => {
  if (status === 'active') return 'bg-success/10 text-success';
  if (status === 'upcoming') return 'bg-primary/10 text-primary';
  return 'bg-muted text-muted-foreground';
};

const LeaguesListPage = () => {
  const [leagues, setLeagues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    document.title = 'Leagues - Tanzania Basketball Federation';
    loadLeagues();
  }, []);

  const loadLeagues = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await leagueService.getAll();
      setLeagues(filterPublicCompetitions(data || []));
    } catch (err) {
      setError(err.message || 'Failed to load leagues');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredLeagues = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return leagues;
    return leagues.filter((league) =>
      [league?.name, league?.season, league?.description]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [leagues, search]);

  return (
    <PublicBrowseShell title="Leagues" subtitle="Basketball leagues organized by Tanzania Basketball Federation">
      <div className="mb-6 max-w-md">
        <Input
          type="search"
          placeholder="Search leagues..."
          iconName="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading leagues...</p>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      ) : filteredLeagues?.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Trophy" size={40} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Leagues Found</h3>
          <p className="text-muted-foreground">Try adjusting your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLeagues.map((league) => (
            <Link
              key={league.id}
              to={`/leagues/${league.id}`}
              className="bg-card rounded-lg border border-border card-shadow p-6 hover:shadow-lg transition-all hover:scale-[1.02]"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-lg bg-orange-600/10 flex items-center justify-center shrink-0">
                  <Icon name="Trophy" size={26} className="text-orange-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-foreground truncate">{league?.name || 'Unnamed League'}</h3>
                  <p className="text-sm text-muted-foreground truncate">{league?.season}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {league?.currentTeams ?? 0} / {league?.maxTeams ?? 0} Teams
                </span>
                <span className={`text-xs px-2 py-1 rounded-full capitalize ${statusBadgeClass(league?.leagueStatus)}`}>
                  {league?.leagueStatus || 'upcoming'}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </PublicBrowseShell>
  );
};

export default LeaguesListPage;
